import { memo, useCallback, useEffect, useMemo } from "react";
import type { IOfficeNew } from "../../../interfaces/models/IOffice";
import { usePersonForm } from "./personForm/PersonFormContext";
import CheckBoxGroup from "../../staff/CheckBoxGroup";
import { formatOfficeText } from "../../../utils/functions";

interface OfficesProps {
  offices: IOfficeNew[];
}

/**
 * Coerces a single item from the `additionalOffices` array — which may arrive as a
 * UUID string, a legacy shortId string/number, or a full office object — into a plain
 * string identifier so it can be matched against office UUIDs.
 */
function coerceOfficeItemToId(item: unknown): string {
  if (typeof item === "string") {
    return item.trim();
  }
  if (typeof item === "number") {
    return String(item);
  }
  if (item !== null && typeof item === "object") {
    const obj = item as Record<string, unknown>;
    if (typeof obj.id === "string" && obj.id.trim()) {
      return obj.id.trim();
    }
    if (obj.shortId != null) {
      return String(obj.shortId);
    }
    if (obj.legacyId != null) {
      return String(obj.legacyId);
    }
  }
  return "";
}

/**
 * Maps a list of raw additionalOffice identifiers (UUID strings, legacy shortId
 * strings/numbers, or full office objects) to canonical office UUID strings using
 * the loaded offices list. Already-valid UUIDs are returned unchanged.
 */
function normalizeToOfficeUuids(ids: unknown[], offices: IOfficeNew[]): string[] {
  if (!offices.length || !ids.length) {
    return ids.map(coerceOfficeItemToId).filter(Boolean);
  }
  const uuidSet = new Set(offices.map((o) => o.id).filter(Boolean) as string[]);
  const shortIdToUuid = new Map<string, string>(
    offices
      .filter((o): o is IOfficeNew & { id: string } => Boolean(o.id) && o.shortId != null)
      .map((o) => [String(o.shortId), o.id]),
  );
  return ids
    .map((raw): string => {
      const id = coerceOfficeItemToId(raw);
      if (!id) {
        return "";
      }
      if (uuidSet.has(id)) {
        return id;
      }
      return shortIdToUuid.get(id) ?? id;
    })
    .filter(Boolean);
}

const OfficesComponent: React.FC<OfficesProps> = ({ offices }) => {
  const { form, setField, touchField } = usePersonForm();

  // Normalize stored IDs (may be legacy shortIds, numbers, or full objects) to office UUIDs.
  const selectedIds = useMemo(
    () => normalizeToOfficeUuids(form.additionalOffices ?? [], offices),
    [form.additionalOffices, offices],
  );

  // When offices load and normalization changed any IDs, persist the corrected UUIDs
  // back to the form so that subsequent saves store the canonical office UUIDs.
  useEffect(() => {
    if (!offices.length) {
      return;
    }
    const raw = form.additionalOffices;
    if (!raw?.length) {
      return;
    }
    const normalized = normalizeToOfficeUuids(raw as unknown[], offices);
    const changed =
      normalized.length !== raw.length ||
      normalized.some((id, i) => id !== (raw[i] as unknown));
    if (changed) {
      setField("additionalOffices", normalized);
    }
  }, [offices, form.additionalOffices, setField]);

  const handleAdditionalOfficesChange = useCallback(
    (next: string[]) => {
      setField("additionalOffices", next);
      touchField("additionalOffices");
    },
    [setField, touchField],
  );

  return (
    <CheckBoxGroup<IOfficeNew>
      title="Additional Offices"
      contentClassName="grid gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6"
      options={offices}
      value={selectedIds}
      onChange={handleAdditionalOfficesChange}
      getOptionValue={(office) => office.id ?? ""}
      getOptionLabel={(office) => formatOfficeText(office)}
      getOptionDisabled={(office) => office.id === form.mainOffice}
    />
  );
};

const Offices = memo(OfficesComponent);
export default Offices;
